import { PubSubService } from '@ohif/core';
import { AccountRoleEnum, SessionService } from './SessionService';
import { ReportAssessmentService } from './ReportAssessmentService';
import { DiagnosisState, DiagnosisStateContext, describeError, getDiagnosisState } from '../utils';
import {
  DiagnosisStatus,
  fetchDiagnosisStatus,
  revokeReport,
  triggerDiagnosis,
} from '../clients/viewer';
import { UnauthorizedException } from '../http';

const EVENTS = {
  DIAGNOSIS_COMPLETED: 'event::sycai:DiagnosisService:diagnosisCompleted',
  DIAGNOSIS_FAILED: 'event::sycai:DiagnosisService:diagnosisFailed',
  DIAGNOSIS_STATE_CHANGED: 'event::sycai:DiagnosisService:diagnosisStateChanged',
  SESSION_LOST: 'event::sycai:DiagnosisService:sessionLost',
} as const;

/**
 * One payload shape per event, beside the names rather than at each subscriber.
 *
 * All three subscribers used to re-declare the shape inline, and all three declared
 * only `studyInstanceUid` — so `state`, which update() has always broadcast on
 * DIAGNOSIS_STATE_CHANGED, was invisible to every one of them. Three independent
 * declarations could not disagree with the broadcast and be caught; one can't disagree
 * at all, because emit() below checks it at the source.
 */
export type DiagnosisEventPayloads = {
  [EVENTS.DIAGNOSIS_COMPLETED]: { studyInstanceUid: string };
  [EVENTS.DIAGNOSIS_FAILED]: { studyInstanceUid: string };
  [EVENTS.DIAGNOSIS_STATE_CHANGED]: { studyInstanceUid: string; state: DiagnosisState };
  [EVENTS.SESSION_LOST]: { studyInstanceUid: string };
};

const INITIAL_CONTEXT: DiagnosisStateContext = {
  role: null,
  isGenerating: false,
  isReportGenerated: false,
  assessmentRequired: false,
};

/**
 * The single source of truth for the diagnosis pipeline (D13). Nothing outside this
 * class holds diagnosis state; the hook subscribes and reads through.
 *
 * develop kept it in two maps and diverged from its own hook. `studyReportMap` was
 * declared, cleared by clearStudyTracking and read by isInProgress — and written by
 * NOTHING, on any branch, so the clear was a no-op and isInProgress always returned
 * undefined (§6 defects 3 and 4). The live cache was the other map. One map here.
 *
 * develop also declared six events, of which two were broadcast only from a
 * never-instantiated event service and one had no subscribers. Four here — three
 * replayed plus SESSION_LOST, which M3.4 D6 added — and DIAGNOSIS_STATE_CHANGED fires
 * only on an ACTUAL change; develop's equivalent fired on every poll tick, because
 * 2c91386759 deleted the comparison (§6 defect 6).
 */
export class DiagnosisService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'diagnosisService',
    create: ({ servicesManager }: { servicesManager: AppTypes.ServicesManager }) =>
      new DiagnosisService(servicesManager),
  };

  /**
   * The emit half of the same contract. `_broadcastEvent` takes `unknown`, so without
   * this the payload types would be asserted at the subscriber and never checked where
   * the payload is actually built — which is the only place a mismatch can start.
   */
  private emit<E extends keyof DiagnosisEventPayloads>(
    event: E,
    payload: DiagnosisEventPayloads[E]
  ): void {
    this._broadcastEvent(event, payload);
  }

  private readonly sessionService: SessionService;
  private readonly reportAssessmentService: ReportAssessmentService;

  private studies = new Map<string, { context: DiagnosisStateContext; state: DiagnosisState }>();

  /**
   * Plan D-4. A study is in this set only while a real diagnosis RUN is outstanding —
   * put there by trigger(), or by observing IN_PROGRESS from the server. Only a
   * resolution while the study is in the set raises a completion event.
   *
   * develop decided this with `wasGenerating = serviceState === PROCESSING`, which
   * worked only because its hook set PROCESSING locally and bypassed the service. With
   * the service as the single source of truth, §5.6's optimistic PROCESSING on submit
   * would satisfy that test and raise a second "Report Generated" dialog every time an
   * assessment was submitted.
   */
  private runsInFlight = new Set<string>();

  /**
   * Per-study request generation. Captured by every async read when the request is
   * issued and checked before any write; trigger, revoke and submitAssessment bump it.
   * A response from a superseded generation is discarded, so an out-of-order arrival is
   * harmless rather than merely unlikely.
   *
   * Not an AbortController: that would need plumbing through HttpClient and the client
   * module, and it cannot help where both requests are legitimate and only the order
   * they resolve in is wrong.
   */
  private generations = new Map<string, number>();

  /**
   * Serving plan D-4: track(), the poller (via useDiagnosisStatus's `refresh` callback)
   * and submitAssessment() all call refresh() directly, so the guard against two
   * in-flight requests for the same study has to live here, where all three converge,
   * rather than in the hook, which sees only the poller's own calls. An unguarded
   * refresh() racing a poll tick could let a slower request's IN_PROGRESS response land
   * after a faster poll's COMPLETED, re-adding the UID to runsInFlight after the
   * completion path had just cleared it, and firing a SECOND DIAGNOSIS_COMPLETED — the
   * duplicate completion dialog D-4 exists to prevent.
   *
   * A Map of PROMISES, not a Set of UIDs. A set version early-returned, so a second
   * caller silently got nothing back: a mode exit and re-entry while a request was
   * outstanding had the new mount's track() deduped away, leaving the button at IDLE
   * and enabled with a diagnosis genuinely running, one click from a duplicate run.
   * Handing back the in-flight promise keeps the property that made the guard
   * necessary — still exactly one round trip per study per overlap — and makes the
   * second caller await the first caller's result.
   *
   * KEYED BY GENERATION, because sharing a request is only correct while it still
   * describes the current world. An unkeyed entry hands submitAssessment's read-back a
   * response issued before the submit landed, so a successful submit reverts to
   * REQUIRES_ASSESSMENT and polling stops with it.
   */
  private refreshesInFlight = new Map<string, { generation: number; request: Promise<void> }>();

  /**
   * The study the viewer is showing, learned from track(). §5.7: the completion-dialog
   * handler compares against this rather than against studyInstanceUIDs, because an
   * extension's onModeEnter receives no study UIDs, no route params, and
   * ExtensionManager.onModeEnter() takes no parameters at all.
   */
  private trackedStudyInstanceUid: string | null = null;

  constructor(servicesManager: AppTypes.ServicesManager) {
    super(EVENTS);

    const { sessionService, reportAssessmentService } = servicesManager.services;

    // Unreachable by construction — index.tsx registers all three in order, five lines
    // apart, and registration.test.ts asserts that order. A plain throw rather than
    // fatal(): §10 risk 3 is that a throw in preRegistration leaves the extension
    // unregistered while the app boots normally, and adding a new fatal() there is
    // exactly what M3 undertook not to do.
    if (!sessionService || !reportAssessmentService) {
      throw new Error(
        '[sycai] DiagnosisService was constructed before sessionService and ' +
          'reportAssessmentService were registered'
      );
    }

    this.sessionService = sessionService;
    this.reportAssessmentService = reportAssessmentService;
  }

  /**
   * A STRING, not an object (plan D-3): React compares it by value, so a re-broadcast
   * that did not change the state cannot cause a render.
   */
  public getState(studyInstanceUid: string): DiagnosisState {
    return this.studies.get(studyInstanceUid)?.state ?? DiagnosisState.IDLE;
  }

  public getTrackedStudy(): string | null {
    return this.trackedStudyInstanceUid;
  }

  /** Called once per study by useDiagnosisStatus. The one unpolled read (D2). */
  public async track(studyInstanceUid: string): Promise<void> {
    this.trackedStudyInstanceUid = studyInstanceUid;
    await this.refresh(studyInstanceUid);
  }

  /**
   * The only network read, and the one every caller shares: a second call for a study
   * already being read awaits the first rather than issuing its own.
   *
   * Never rejects for any error it originates, because `read` below never rejects for one
   * either: usePolling's setInterval body does `void callbackRef.current()` with no
   * .catch(), so a rejection here would be an unhandled rejection every tick (M2 debt
   * register). A throwing subscriber can still reject it through `emit` -> `_broadcastEvent`
   * (pubSubServiceInterface.ts:83-95 invokes listeners inline, no try/catch) — confirmed
   * unreachable today, since DIAGNOSIS_STATE_CHANGED's only subscriber does a UID
   * comparison and a `setState`, but this is a property of that subscriber, not a
   * guarantee this method makes.
   *
   * The entry clears in a `.finally()`, which only runs once the request SETTLES. A
   * request that neither resolves nor rejects would hold it forever with no way out
   * short of a remount. HttpClient's default request timeout (HttpClient.ts) is what
   * guarantees that settlement — axios's own default is no timeout at all.
   */
  public async refresh(studyInstanceUid: string): Promise<void> {
    const generation = this.generationOf(studyInstanceUid);
    const inFlight = this.refreshesInFlight.get(studyInstanceUid);

    if (inFlight && inFlight.generation === generation) {
      return inFlight.request;
    }

    const request = this.read(studyInstanceUid, generation).finally(() => {
      // Only if the entry is still this request's. A newer generation may have replaced
      // it while this one was out, and deleting that one would let the next caller issue
      // a duplicate against a request that is still live.
      if (this.refreshesInFlight.get(studyInstanceUid)?.request === request) {
        this.refreshesInFlight.delete(studyInstanceUid);
      }
    });

    this.refreshesInFlight.set(studyInstanceUid, { generation, request });

    return request;
  }

  private async read(studyInstanceUid: string, generation: number): Promise<void> {
    try {
      const status = await fetchDiagnosisStatus(studyInstanceUid);

      // Before any write, and before readReport specifically: that call writes into
      // ReportAssessmentService, which has no counter of its own, so the cheapest way
      // not to write stale rows there is not to ask.
      if (this.isStale(studyInstanceUid, generation)) {
        return;
      }

      if (status === DiagnosisStatus.IN_PROGRESS) {
        this.runsInFlight.add(studyInstanceUid);
        this.update(studyInstanceUid, { isGenerating: true });
        return;
      }

      // COMPLETED, FAILED and NOT_FOUND all travel this path. NOT_FOUND resolves to
      // IDLE because no report exists — develop's behaviour (diagnosis.service.ts:114),
      // kept, and ranked third on the stub README's likeliest divergences.
      const resolution = await this.readReport(studyInstanceUid, status);

      // Again, because readReport awaits. The write below is what clears isGenerating,
      // and the Set.delete after it is what retires a live run's token.
      if (this.isStale(studyInstanceUid, generation)) {
        return;
      }

      // State FIRST, then the completion event, so a subscriber that reads the service
      // sees the resolved state rather than the one it just left.
      this.update(studyInstanceUid, { isGenerating: false, ...resolution });

      // Set.delete returns whether the entry was there, so this both tests and clears.
      if (!this.runsInFlight.delete(studyInstanceUid)) {
        return;
      }

      if (status === DiagnosisStatus.COMPLETED) {
        this.emit(EVENTS.DIAGNOSIS_COMPLETED, { studyInstanceUid });
      }

      if (status === DiagnosisStatus.FAILED) {
        this.emit(EVENTS.DIAGNOSIS_FAILED, { studyInstanceUid });
      }
    } catch (error) {
      this.report(studyInstanceUid, 'reading the diagnosis status', error);
    }
  }

  /**
   * What a resolved status says about the report, as a patch for update().
   *
   * Resolved OUTSIDE read()'s try block, and not awaited before the state moves. D-9
   * deliberately lets fetchReportAssessments throw, so a persistent
   * error on studies/{uid}/reports/assessments — a 500, or a 404 for a FAILED run with
   * no report row — skipped update() and the runsInFlight clear alike: the state stayed
   * PROCESSING, no broadcast arrived, so the hook never stopped polling, the button read
   * "Processing…" for the rest of the session and the failure dialog this milestone
   * exists to show never appeared. The run is now always resolved and always broadcast;
   * a broken backend still looks broken through the one console line, it just cannot
   * strand the state machine as well.
   *
   * An empty patch leaves isReportGenerated and assessmentRequired at their last known
   * values, because update() merges over the previous context — that is D-9's "leaves
   * the state unchanged", now applied to the report flags only rather than to the run.
   */
  private async readReport(
    studyInstanceUid: string,
    status: DiagnosisStatus
  ): Promise<Partial<DiagnosisStateContext>> {
    // A FAILED run produced no report, so there is nothing to read and no read to fail.
    if (status === DiagnosisStatus.FAILED) {
      return { isReportGenerated: false, assessmentRequired: false };
    }

    try {
      const { isReportGenerated, assessmentRequired } =
        await this.reportAssessmentService.refreshStudy(studyInstanceUid);

      return { isReportGenerated, assessmentRequired };
    } catch (error) {
      this.report(studyInstanceUid, 'reading the report assessments', error);
      return {};
    }
  }

  /**
   * The optimistic PROCESSING lands BEFORE the await, as submitAssessment's does
   * (§5.6). A trigger POST takes seconds against a real backend, and until the state
   * moves the button still reads "Diagnose" and is still enabled, so a second click
   * starts a second backend run.
   *
   * The optimistic PROCESSING is itself what starts the polling that corrects it, so a
   * POST that fails returns to IDLE on the first tick whose STATUS READ SUCCEEDS — not
   * unconditionally. If the backend is down, both calls fail, and the button holds
   * PROCESSING and polls every five seconds for the session, where before this change a
   * failed trigger left it at IDLE. That is the deliberate trade: a button that
   * over-reports a run against a dead backend, rather than one that invites a duplicate
   * run against a live but slow one.
   *
   * runsInFlight is armed only by a RESOLVED trigger, never optimistically: D-4 admits
   * exactly two ways in, a resolved trigger() and an observed IN_PROGRESS, and arming it
   * for a POST that never resolved would raise a completion dialog for a run that never
   * started.
   *
   * The generation is bumped twice on the success path, and both are load-bearing. The
   * first discards a read that was already in flight when the click landed, which
   * answers the pre-trigger world and would overwrite the optimistic PROCESSING below.
   * The second discards a read issued DURING the POST's window: a poll tick can read
   * NOT_FOUND, because the backend has not registered the run yet, and land after the
   * POST resolved — putting the state back to IDLE, clearing the run token, and leaving
   * the button enabled with a run genuinely in progress.
   *
   * The re-assert below does not recover that case on its own, whatever the symmetry
   * suggests: it only does when the stale read happens to land first, and nothing orders
   * them. The bump is what orders them.
   */
  public async trigger(studyInstanceUid: string): Promise<void> {
    this.bumpGeneration(studyInstanceUid);
    this.update(studyInstanceUid, { isGenerating: true });

    try {
      await triggerDiagnosis(studyInstanceUid);
    } catch (error) {
      this.report(studyInstanceUid, 'triggering the diagnosis', error);
      return;
    }

    this.bumpGeneration(studyInstanceUid);
    this.runsInFlight.add(studyInstanceUid);
    this.update(studyInstanceUid, { isGenerating: true });
  }

  /**
   * D5, fail CLOSED. sessionService.getRole() is null after a failed verifySession and
   * can be an unrecognised string when the backend adds a role, and neither is an
   * admin. No gate here is a security boundary — enforcement is the endpoint receiving
   * the same session cookie — so the cost of failing closed is an admin losing a button
   * on a transport blip, against showing Revoke to every role.
   *
   * develop compared the bare string 'ENVIRONMENT_ADMIN' here (§6 defect 8).
   *
   * Deliberately NOT added to runsInFlight: a revoke is not a diagnosis run, so its
   * resolution must not raise "Report Generated". If the backend responds by re-running
   * the diagnosis — the open question at the stub README's divergence 5 — the next poll
   * observes IN_PROGRESS and arms the run properly. D-4's set is what gates the dialog,
   * and nothing here touches it.
   *
   * No optimistic transition, unlike trigger(). trigger()'s is worth its cost because a
   * second click there starts a second backend run; a second revoke withdraws an
   * already-withdrawn report. The cost is real: the catch below logs and returns, so an
   * optimistic PROCESSING left behind means the button reads "Processing…" and polls a
   * dead backend for the rest of the session.
   *
   * The read-back moves the state instead, and it is not optional — polling runs only in
   * PROCESSING, so an admin sitting in REVOCABLE is not polling and nothing else would
   * notice the report had gone.
   */
  public async revoke(studyInstanceUid: string): Promise<void> {
    if (this.sessionService.getRole() !== AccountRoleEnum.ENVIRONMENT_ADMIN) {
      return;
    }

    this.bumpGeneration(studyInstanceUid);

    try {
      await revokeReport(studyInstanceUid);
    } catch (error) {
      this.report(studyInstanceUid, 'revoking the report', error);
      return;
    }

    this.bumpGeneration(studyInstanceUid);

    await this.refresh(studyInstanceUid);
  }

  /**
   * §5.6: the service moves to PROCESSING BEFORE awaiting the network call, so the
   * transition is immediate — but immediate IN THE SERVICE, which is what makes it
   * observable to the button and to the completion-dialog subscriber alike. develop got
   * the same feel from a local setState that diverged from the service until the next
   * tick (D13).
   */
  public async submitAssessment(studyInstanceUid: string): Promise<void> {
    this.bumpGeneration(studyInstanceUid);
    this.update(studyInstanceUid, { isGenerating: true });

    try {
      await this.reportAssessmentService.submitStudy(studyInstanceUid);
    } catch (error) {
      this.report(studyInstanceUid, 'submitting the assessment', error);
    }

    // Bumped whether or not the submit threw. On success it is what makes the read-back
    // below a fresh request rather than a tick issued before the submit landed; on
    // failure it costs one extra round trip and never lies.
    this.bumpGeneration(studyInstanceUid);

    // Unconditional: on success this reads back the post-submit shape, and on failure
    // it puts the optimistic PROCESSING back to whatever is true.
    await this.refresh(studyInstanceUid);
  }

  /**
   * Domain state is dropped on mode exit and nowhere else: there is deliberately no
   * per-study eviction a component can call. Evicting from a component's cleanup is what
   * stranded this service before — read() writes after both of its awaits, so a response
   * landing after the eviction resurrected the entry, and a remount read the gap as IDLE
   * and offered a second run for a diagnosis already in progress.
   *
   * A study switch is a mode exit, and ExtensionManager calls this on every registered
   * service. PubSubService.reset() is not an alternative: it clears listeners, which
   * would take the hook's own subscription with it.
   */
  public onModeExit(): void {
    // Bumped, never cleared: clearing puts every counter back to zero, and the next
    // trigger returns it to one — making a request from the previous mode entry current
    // again.
    const touched = new Set([
      ...this.studies.keys(),
      ...this.refreshesInFlight.keys(),
      ...this.generations.keys(),
    ]);

    touched.forEach(studyInstanceUid => this.bumpGeneration(studyInstanceUid));

    this.studies.clear();
    this.runsInFlight.clear();

    // Safe only because of the bump above. Dropping an entry while its request is still
    // out lets the next track() start a second live request for the same study; since a
    // superseded request can no longer write, that is a wasted round trip, not a race.
    this.refreshesInFlight.clear();

    this.trackedStudyInstanceUid = null;
  }

  private generationOf(studyInstanceUid: string): number {
    return this.generations.get(studyInstanceUid) ?? 0;
  }

  private bumpGeneration(studyInstanceUid: string): void {
    this.generations.set(studyInstanceUid, this.generationOf(studyInstanceUid) + 1);
  }

  /** True once anything has superseded the generation this response was issued under. */
  private isStale(studyInstanceUid: string, generation: number): boolean {
    return this.generationOf(studyInstanceUid) !== generation;
  }

  /**
   * The single write path. Re-derives the role on every update rather than caching it
   * (D6): sessionService.getRole() is populated during preRegistration and is the only
   * role source M3 uses — develop's useUserAuthentication() carried it instead, and
   * DiagnosisButton.tsx:17 destructured `user` and never used it.
   */
  private update(studyInstanceUid: string, patch: Partial<DiagnosisStateContext>): void {
    const previous = this.studies.get(studyInstanceUid);

    const context: DiagnosisStateContext = {
      ...INITIAL_CONTEXT,
      ...previous?.context,
      ...patch,
      role: this.sessionService.getRole(),
    };

    const state = getDiagnosisState(context);
    this.studies.set(studyInstanceUid, { context, state });

    // ONLY on an actual change. develop broadcast unconditionally, so its equivalent
    // event fired on every poll tick — and because its hook restarted polling inside
    // that handler, polling was self-sustaining and ran continuously from mount
    // regardless of state (D2, §6 defects 5 and 6).
    if (previous?.state === state) {
      return;
    }

    // The payload carries the UID and the state, never an account or a token (§6.1).
    this.emit(EVENTS.DIAGNOSIS_STATE_CHANGED, { studyInstanceUid, state });
  }

  /**
   * One line, no body echo — describeError decides how much of the error is safe to
   * print, and is the same rule as index.tsx's boot diagnostic.
   *
   * D6's second half lives here rather than in read(), because all five catches in this
   * class funnel through it and a 401 on any of them means the same thing. The uid is
   * threaded in only for the payload; the log line does not carry it.
   */
  private report(studyInstanceUid: string, action: string, error: unknown): void {
    console.error(
      `[sycai] ${action} failed ${describeError(error)}; the button keeps its last state`
    );

    if (error instanceof UnauthorizedException) {
      this.emit(EVENTS.SESSION_LOST, { studyInstanceUid });
    }
  }
}

/**
 * A declaration merge, not a class member — TypeScript folds an interface with a
 * class's name into that class's instance type, so `subscribe` narrows for every
 * caller exactly as a class field would, but nothing here compiles to a runtime
 * assignment. That matters because PubSubService assigns `subscribe` itself, in its
 * own constructor (pubSubServiceInterface.ts:107-114); a class field of the same
 * name — even a `declare`d one — is still class-member syntax, and this repo's root
 * babel.config.js orders `@babel/plugin-transform-class-properties` before
 * `@babel/plugin-transform-typescript`, so the class-properties transform reaches a
 * `declare subscribe` field before typescript's transform has stripped it and Jest
 * fails to parse the file at all (measured: swapping in a `declare subscribe` class
 * field here breaks `ordering.test.ts` and `registration.test.ts` with "TypeScript
 * 'declare' fields must first be transformed by @babel/plugin-transform-typescript").
 * No other class in this tree declares a class field this way, which is why nothing
 * had hit that ordering bug before. An interface merge never reaches the
 * class-properties transform at all — preset-typescript erases the whole
 * declaration as an ordinary statement — so the failure mode is closed by
 * construction rather than by relying on `declare` being honoured correctly.
 *
 * The INTERSECTION with the base type is still deliberate, for the same reason it
 * would be on a class member: a narrower property type alone is not assignable to
 * PubSubService's under strictFunctionTypes — parameters are contravariant, and
 * `string` is not assignable to the key union. Intersecting keeps the override
 * legal, and overload resolution tries the specific member first, so calls with a
 * known event still get the narrow payload.
 */
export interface DiagnosisService {
  subscribe: (<E extends keyof DiagnosisEventPayloads>(
    eventName: E,
    callback: (data: DiagnosisEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}
